import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, 
  CheckCircle2, 
  TrendingUp, 
  Zap, 
  ShieldCheck, 
  ArrowRight,
  DollarSign,
  Users,
  Crown,
  Stethoscope,
  Boxes,
  Syringe,
  Building2
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useIsPlayStore } from '../utils/playstore';

export type PaywallInitialPlan = 
  | 'monthly' 
  | 'annual' 
  | 'specialist' 
  | 'specialist_annual' 
  | 'specialist_anual'
  | 'specialist_anual_pix'
  | 'specialist_pix'
  | 'pro' 
  | 'premium' 
  | 'pro_anual' 
  | 'pro_anual_pix'
  | 'pro_pix'
  | 'especialista' 
  | 'especialista_anual' 
  | 'especialista_anual_pix'
  | 'free'
  | 'enterprise'
  | 'enterprise_annual'
  | 'enterprise_anual'
  | 'enterprise_anual_pix'
  | 'enterprise_pix'
  | 'empresarial'
  | 'empresarial_anual'
  | 'empresarial_anual_pix'
  | 'empresa'
  | 'empresa_annual'
  | 'empresa_anual'
  | 'empresa_anual_pix'
  | 'empresa_pix';

export interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPlan?: PaywallInitialPlan;
}

type PlanType = 
  | 'enterprise_anual_pix' 
  | 'enterprise_annual' 
  | 'enterprise'
  | 'specialist_anual_pix' 
  | 'specialist_annual' 
  | 'specialist' 
  | 'pro_anual_pix'
  | 'pro_annual' 
  | 'monthly';

const resolvePlanType = (plan?: string): PlanType => {
  if (!plan) return 'specialist_anual_pix';
  const p = String(plan).toLowerCase().trim();
  if (p === 'empresa_anual_pix' || p === 'empresa_pix' || p === 'enterprise_anual_pix' || p === 'enterprise_pix' || p === 'empresarial_anual_pix' || p === 'empresarial_pix') {
    return 'enterprise_anual_pix';
  }
  if (p === 'empresa_anual' || p === 'empresa_annual' || p === 'enterprise_annual' || p === 'enterprise_anual' || p === 'empresarial_anual' || p === 'empresarial_annual') {
    return 'enterprise_annual';
  }
  if (p === 'empresa' || p === 'empresa_mensal' || p === 'enterprise' || p === 'empresarial' || p === 'enterprise_mensal' || p === 'empresarial_mensal') {
    return 'enterprise';
  }
  if (p === 'specialist_anual_pix' || p === 'especialista_anual_pix' || p === 'pix') {
    return 'specialist_anual_pix';
  }
  if (p === 'specialist_annual' || p === 'especialista_anual' || p === 'premium_anual' || p === 'premium_annual') {
    return 'specialist_annual';
  }
  if (p === 'specialist' || p === 'premium' || p === 'especialista' || p === 'specialist_monthly' || p === 'especialista_mensal') {
    return 'specialist';
  }
  if (p === 'pro_anual_pix' || p === 'pro_pix') {
    return 'pro_anual_pix';
  }
  if (p === 'pro_anual' || p === 'pro_annual' || p === 'annual') {
    return 'pro_annual';
  }
  if (p === 'monthly' || p === 'pro' || p === 'pro_mensal' || p === 'pro_monthly') {
    return 'monthly';
  }
  return 'specialist_anual_pix';
};

export default function PaywallModal({ isOpen, onClose, initialPlan = 'specialist_annual' }: PaywallModalProps) {
  const isPlayStore = useIsPlayStore();
  const [selectedPlan, setSelectedPlan] = React.useState<PlanType>(() => resolvePlanType(initialPlan));
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showBasicPro, setShowBasicPro] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedPlan(resolvePlanType(initialPlan));
      setErrorMessage(null);
    }
  }, [isOpen, initialPlan]);

  const planConfigs = {
    enterprise_anual_pix: {
      id: 'enterprise_anual_pix',
      name: 'Plano Empresa Anual (PIX à Vista)',
      tag: 'PIX R$ 990,00 à vista',
      price: 'R$ 990,00',
      period: 'ano à vista no PIX',
      subtext: 'Economia de R$ 198,00/ano frente ao mensal',
      buttonText: 'Garantir Plano Empresa no PIX (R$ 990)',
      isEnterprise: true,
      isSpecialist: false,
      isPix: true,
      value: 990.00
    },
    enterprise_annual: {
      id: 'enterprise_anual',
      name: 'Plano Empresa Anual (10x no Cartão)',
      tag: '10x R$ 99,00 (R$ 990/ano)',
      price: '10x R$ 99,00',
      period: 'sem juros no cartão',
      subtext: 'R$ 990,00/ano em até 10 parcelas',
      buttonText: 'Desbloquear em 10x de R$ 99,00',
      isEnterprise: true,
      isSpecialist: false,
      isPix: false,
      value: 990.00
    },
    enterprise: {
      id: 'enterprise',
      name: 'Plano Empresa Mensal (Flexível)',
      tag: 'R$ 99,00/mês',
      price: 'R$ 99,00',
      period: 'mês',
      subtext: 'Sem fidelidade • Cancela quando quiser',
      buttonText: 'Desbloquear Plano Empresa Mensal (R$ 99,00)',
      isEnterprise: true,
      isSpecialist: false,
      isPix: false,
      value: 99.00
    },
    specialist_anual_pix: {
      id: 'especialista_anual_pix',
      name: 'Especialista Anual (PIX à Vista)',
      tag: 'PIX R$ 499,00 à vista',
      price: 'R$ 499,00',
      period: 'ano à vista no PIX',
      subtext: 'Economia de R$ 99,80/ano frente ao mensal',
      buttonText: 'Garantir Acesso no PIX (R$ 499)',
      isEnterprise: false,
      isSpecialist: true,
      isPix: true,
      value: 499.00
    },
    specialist_annual: {
      id: 'especialista_anual',
      name: 'Especialista Anual (10x no Cartão)',
      tag: '10x R$ 49,90 (R$ 499/ano)',
      price: '10x R$ 49,90',
      period: 'sem juros no cartão',
      subtext: 'R$ 499,00/ano em até 10 parcelas',
      buttonText: 'Desbloquear em 10x de R$ 49,90',
      isEnterprise: false,
      isSpecialist: true,
      isPix: false,
      value: 499.00
    },
    specialist: {
      id: 'especialista',
      name: 'Especialista Mensal (Flexível)',
      tag: 'R$ 49,90/mês',
      price: 'R$ 49,90',
      period: 'mês',
      subtext: 'Sem fidelidade • Cancela quando quiser',
      buttonText: 'Desbloquear Especialista Mensal (R$ 49,90)',
      isEnterprise: false,
      isSpecialist: true,
      isPix: false,
      value: 49.90
    },
    pro_anual_pix: {
      id: 'pro_anual_pix',
      name: 'R-MAX Pro Anual (PIX à Vista)',
      tag: 'PIX R$ 195,00 à vista',
      price: 'R$ 195,00',
      period: 'ano à vista no PIX',
      subtext: 'Economia de R$ 43,80/ano frente ao mensal',
      buttonText: 'Garantir R-MAX Pro no PIX (R$ 195)',
      isEnterprise: false,
      isSpecialist: false,
      isPix: true,
      value: 195.00
    },
    pro_annual: {
      id: 'pro_anual',
      name: 'R-MAX Pro Anual (Calculadora)',
      tag: '10x R$ 19,50 (R$ 195/ano)',
      price: '10x 19,50',
      period: 'sem juros',
      subtext: 'R$ 195/ano à vista',
      buttonText: 'Desbloquear Pro Anual',
      isEnterprise: false,
      isSpecialist: false,
      isPix: false,
      value: 195.00
    },
    monthly: {
      id: 'pro',
      name: 'R-MAX Pro Mensal (Calculadora)',
      tag: 'R$ 19,90/mês',
      price: 'R$ 19,90',
      period: 'mês',
      subtext: 'Calculadora sem limites',
      buttonText: 'Desbloquear Pro Mensal',
      isEnterprise: false,
      isSpecialist: false,
      isPix: false,
      value: 19.90
    }
  };

  const currentPlan = planConfigs[selectedPlan] || planConfigs.specialist_anual_pix;

  const iniciarFluxo = async (plano: string) => {
    setIsLoading(true);
    setErrorMessage(null);

    const fetchWithTimeout = async (url: string, options: RequestInit, timeoutMs = 10000) => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    // Dispara o evento para o Google Analytics se configurado
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'clique_paywall', {
        'plano_escolhido': plano,
        'valor': currentPlan.value
      });
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setErrorMessage("Você precisa estar conectado para realizar a assinatura.");
        setIsLoading(false);
        return;
      }

      const response = await fetchWithTimeout(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ plano })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Erro ao gerar link de pagamento.");
      }

      const data = await response.json();
      if (data?.init_point) {
        window.location.href = data.init_point;
      } else {
        throw new Error("Link de pagamento não retornou pelo servidor.");
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.name === 'AbortError' ? 'Tempo esgotado ao conectar com o servidor. Tente novamente.' : err.message || "Erro ao conectar com o Mercado Pago. Tente novamente.");
      setIsLoading(false);
    }
  };

  const features = currentPlan.isEnterprise ? [
    {
      icon: <Stethoscope className="h-5 w-5 text-blue-400" />,
      title: "Gestão Clínica Médica e Vet Multi-Pet",
      desc: "Prontuário SOAP com valor legal, anamnese, sinais vitais, prescrição formal em PDF e alerta anti-troca."
    },
    {
      icon: <Syringe className="h-5 w-5 text-emerald-400" />,
      title: "Central de Retorno de Vacinas no WhatsApp",
      desc: "Disparos de reforço via WhatsApp com pré-filtro da agenda online sem custo de API."
    },
    {
      icon: <Boxes className="h-5 w-5 text-amber-400" />,
      title: "Estoque com Lotes, Validade & Balancete",
      desc: "Baixa automática FIFO em atendimentos, extrato analítico de insumos e balancete financeiro."
    },
    {
      icon: <Building2 className="h-5 w-5 text-indigo-400" />,
      title: "Cofre DICOM no R2 & Portal do Contador LGPD",
      desc: "Upload direto ao Cloudflare R2 com guarda legal de 20 anos (CFMV/Lei 13.787) e portal seguro para contabilidade."
    }
  ] : [
    {
      icon: <Zap className="h-5 w-5 text-amber-400" />,
      title: "Orçamentos Rápidos e Modo Direto",
      desc: "Gere propostas ilimitadas por texto, foto ou diretamente pelo catálogo sem limites."
    },
    {
      icon: <TrendingUp className="h-5 w-5 text-emerald-400" />,
      title: "Centro de Custos & Valor da sua Hora",
      desc: "Cadastre materiais, receitas e saiba exatamente quanto cobrar para nunca ter prejuízo."
    },
    {
      icon: <ShieldCheck className="h-5 w-5 text-blue-400" />,
      title: "Gestão Completa do Especialista",
      desc: "Ordens de serviço com fotos, assinatura digital na tela, recibos PIX, agenda online e clientes no mapa."
    },
    {
      icon: <Users className="h-5 w-5 text-indigo-400" />,
      title: "Minha Equipe (Até 10 Colaboradores Inclusos)",
      desc: "Convide vendedores e técnicos com travas de segurança. Margem de lucro oculta (segredo comercial) e orçamentos ilimitados para toda a equipe."
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/80 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-[95%] sm:w-full max-w-lg bg-zinc-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/10 max-h-[85vh] md:max-h-[90vh] flex flex-col m-4"
          >
            {isPlayStore ? (
              <div className="p-6 sm:p-8 flex flex-col items-center text-center space-y-6 relative">
                <button 
                  onClick={onClose}
                  className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-zinc-400"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="w-16 h-16 rounded-3xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand mt-4">
                  <Zap className="h-8 w-8 animate-pulse" />
                </div>

                <div className="space-y-3">
                  <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
                    Recurso dos Planos Pro, Especialista & Empresa
                  </h2>
                  <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed max-w-sm mx-auto">
                    As assinaturas são feitas pelo nosso site.
                  </p>
                  <p className="text-zinc-400 text-xs leading-relaxed max-w-sm mx-auto">
                    Abra o navegador de internet do seu celular ou computador e entre no nosso site para escolher seu plano.
                  </p>
                </div>

                <button 
                  onClick={onClose}
                  className="w-full py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-2xl transition-all uppercase tracking-widest text-xs cursor-pointer active:scale-95"
                >
                  Fechar
                </button>
              </div>
            ) : (
              <>
                {/* Header: Dynamic Theme (Sticky Top) */}
                <div className="relative shrink-0 p-6 pb-2 text-center bg-linear-to-b from-zinc-800 to-zinc-900 border-b border-white/5">
                  <button 
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-zinc-400 cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </button>

                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-3 group cursor-default transition-all ${
                    currentPlan.isEnterprise
                      ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
                      : currentPlan.isSpecialist
                        ? 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                        : 'bg-brand/10 border-brand/20 text-brand'
                  }`}>
                    <Zap className={`h-3 w-3 animate-pulse ${
                      currentPlan.isEnterprise 
                        ? 'text-blue-400' 
                        : currentPlan.isSpecialist 
                          ? 'text-orange-400' 
                          : 'text-brand'
                    }`} />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                      {currentPlan.isEnterprise 
                        ? '💎 R-MAX CLÍNICAS & GESTÃO EMPRESA' 
                        : currentPlan.isSpecialist 
                          ? '👑 R-MAX ESPECIALISTA PREMIUM' 
                          : '⚡ R-MAX PRO'}
                    </span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight">
                    {currentPlan.isEnterprise ? (
                      <>
                        Clínicas Médicas, Vet & <br />
                        <span className="text-blue-400">Plano Empresa Completo.</span>
                      </>
                    ) : currentPlan.isSpecialist ? (
                      <>
                        Tudo para organizar seu negócio <br />
                        <span className="text-orange-400">e garantir o seu lucro.</span>
                      </>
                    ) : (
                      <>
                        Pare de pagar para <br />
                        <span className="text-brand">trabalhar.</span>
                      </>
                    )}
                  </h2>
                  <p className="text-zinc-400 text-xs sm:text-sm max-w-xs mx-auto leading-relaxed">
                    {currentPlan.isEnterprise
                      ? 'Prontuário SOAP legal, Central de Retorno de Vacinas, Estoque com Lotes e Cofre DICOM no R2:'
                      : currentPlan.isSpecialist 
                        ? 'Desbloqueie assinatura do cliente no celular, agenda, cadastro de clientes, ordens de serviço com fotos e orçamentos sem limite:' 
                        : 'A forma mais fácil de calcular seu preço certo e não ter prejuízo. Escolha seu plano:'}
                  </p>
                </div>

                {/* Scrollable Body */}
                <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
                  {/* Seletor Rápido de Nível (Empresa vs Especialista) */}
                  <div className="flex items-center justify-center p-1 bg-zinc-950/80 rounded-2xl border border-white/10 gap-1">
                    <button
                      type="button"
                      onClick={() => setSelectedPlan('enterprise_anual_pix')}
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        currentPlan.isEnterprise 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      <Crown className="h-3.5 w-3.5" />
                      <span>Plano Empresa (R$ 99)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPlan('specialist_anual_pix')}
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        currentPlan.isSpecialist 
                          ? 'bg-orange-600 text-white shadow-md' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      <Zap className="h-3.5 w-3.5" />
                      <span>Especialista (R$ 49,90)</span>
                    </button>
                  </div>

                  {/* Features List */}
                  <div className="space-y-2.5">
                    {features.map((f, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                          {f.icon}
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-xs">{f.title}</h4>
                          <p className="text-[11px] text-zinc-500 leading-relaxed">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Seletor de Planos: Anual vs Mensal */}
                  <div className="space-y-3 pt-1">
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest text-center">
                      Escolha como deseja desbloquear seu acesso:
                    </p>

                    {currentPlan.isEnterprise ? (
                      /* BLINDAGEM ENTERPRISE: ANUAL (10x ou PIX) VS MENSAL */
                      <>
                        <div className="p-3.5 rounded-3xl border-2 border-blue-500/80 bg-linear-to-b from-blue-950/40 via-zinc-900 to-zinc-950 relative shadow-xl ring-1 ring-blue-500/30 space-y-2.5">
                          <div className="absolute -top-3 left-4 bg-linear-to-r from-blue-500 via-indigo-500 to-blue-600 text-white text-[9px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                            <Crown className="h-2.5 w-2.5 fill-white" />
                            <span>MELHOR CUSTO-BENEFÍCIO • 2 MESES GRÁTIS</span>
                          </div>

                          <div className="flex items-center justify-between pt-1">
                            <div>
                              <p className="text-xs font-black text-white flex items-center gap-1.5">
                                💎 Clínicas & Gestão Empresa Anual
                              </p>
                              <p className="text-[10px] text-zinc-400">12 meses de acesso completo a clínicas, estoque e contabilidade</p>
                            </div>
                          </div>

                          {/* Opções dentro do Anual Enterprise: PIX vs 10x Cartão */}
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            {/* Opção PIX Enterprise */}
                            <button
                              type="button"
                              onClick={() => setSelectedPlan('enterprise_anual_pix')}
                              className={`p-2.5 rounded-2xl border text-left transition-all cursor-pointer relative ${
                                selectedPlan === 'enterprise_anual_pix'
                                  ? 'bg-blue-950/70 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.35)] ring-2 ring-blue-500/50'
                                  : 'bg-zinc-900/80 border-zinc-800 hover:border-zinc-700'
                              }`}
                            >
                              <div className="absolute -top-2 right-1.5 bg-emerald-500 text-black text-[7px] font-black px-1.5 py-0.2 rounded-full uppercase">
                                -17% OFF
                              </div>
                              <p className="text-[8px] text-blue-400 font-bold uppercase tracking-wider">PAGAMENTO PIX</p>
                              <p className="text-sm font-black text-white">R$ 990,00</p>
                              <p className="text-[8px] text-zinc-400">à vista no PIX</p>
                            </button>

                            {/* Opção Cartão 10x Enterprise */}
                            <button
                              type="button"
                              onClick={() => setSelectedPlan('enterprise_annual')}
                              className={`p-2.5 rounded-2xl border text-left transition-all cursor-pointer ${
                                selectedPlan === 'enterprise_annual'
                                  ? 'bg-indigo-950/70 border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.35)] ring-2 ring-indigo-500/50'
                                  : 'bg-zinc-900/80 border-zinc-800 hover:border-zinc-700'
                              }`}
                            >
                              <p className="text-[8px] text-indigo-400 font-bold uppercase tracking-wider">CARTÃO EM 10x</p>
                              <p className="text-sm font-black text-white">10x 99,00</p>
                              <p className="text-[8px] text-zinc-400">sem juros no cartão</p>
                            </button>
                          </div>
                        </div>

                        {/* Opção Empresa Mensal */}
                        <button
                          type="button"
                          onClick={() => setSelectedPlan('enterprise')}
                          className={`w-full p-3 rounded-2xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                            selectedPlan === 'enterprise'
                              ? 'bg-zinc-800/90 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.25)] ring-2 ring-blue-500/40'
                              : 'bg-zinc-950/40 border-zinc-800/60 hover:border-zinc-700'
                          }`}
                        >
                          <div>
                            <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider">PLANO MENSAL FLEXÍVEL</p>
                            <p className="text-xs font-black text-white">Plano Empresa Mensal</p>
                            <p className="text-[9px] text-zinc-500">Sem fidelidade • Cancela quando quiser</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black text-white">R$ 99,00</p>
                            <p className="text-[9px] text-zinc-400">/mês</p>
                          </div>
                        </button>
                      </>
                    ) : (
                      /* BLINDAGEM ESPECIALISTA */
                      <>
                        <div className="p-3.5 rounded-3xl border-2 border-orange-500/80 bg-linear-to-b from-orange-950/40 via-zinc-900 to-zinc-950 relative shadow-xl ring-1 ring-orange-500/30 space-y-2.5">
                          <div className="absolute -top-3 left-4 bg-linear-to-r from-amber-500 via-orange-500 to-amber-600 text-black text-[9px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                            <Zap className="h-2.5 w-2.5 fill-black" />
                            <span>MELHOR CUSTO-BENEFÍCIO • 2 MESES GRÁTIS</span>
                          </div>

                          <div className="flex items-center justify-between pt-1">
                            <div>
                              <p className="text-xs font-black text-white flex items-center gap-1.5">
                                👑 Especialista Premium Anual
                              </p>
                              <p className="text-[10px] text-zinc-400">12 meses de acesso irrestrito ao ecossistema</p>
                            </div>
                          </div>

                          {/* Opções dentro do Anual: PIX vs 10x Cartão */}
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setSelectedPlan('specialist_anual_pix')}
                              className={`p-2.5 rounded-2xl border text-left transition-all cursor-pointer relative ${
                                selectedPlan === 'specialist_anual_pix'
                                  ? 'bg-emerald-950/60 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)] ring-2 ring-emerald-500/50'
                                  : 'bg-zinc-900/80 border-zinc-800 hover:border-zinc-700'
                              }`}
                            >
                              <div className="absolute -top-2 right-1.5 bg-emerald-500 text-black text-[7px] font-black px-1.5 py-0.2 rounded-full uppercase">
                                -17% OFF
                              </div>
                              <p className="text-[8px] text-emerald-400 font-bold uppercase tracking-wider">PAGAMENTO PIX</p>
                              <p className="text-sm font-black text-white">R$ 499,00</p>
                              <p className="text-[8px] text-zinc-400">à vista no PIX</p>
                            </button>

                            <button
                              type="button"
                              onClick={() => setSelectedPlan('specialist_annual')}
                              className={`p-2.5 rounded-2xl border text-left transition-all cursor-pointer ${
                                selectedPlan === 'specialist_annual'
                                  ? 'bg-orange-950/60 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.3)] ring-2 ring-orange-500/50'
                                  : 'bg-zinc-900/80 border-zinc-800 hover:border-zinc-700'
                              }`}
                            >
                              <p className="text-[8px] text-orange-400 font-bold uppercase tracking-wider">CARTÃO EM 10x</p>
                              <p className="text-sm font-black text-white">10x 49,90</p>
                              <p className="text-[8px] text-zinc-400">sem juros no cartão</p>
                            </button>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setSelectedPlan('specialist')}
                          className={`w-full p-3 rounded-2xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                            selectedPlan === 'specialist'
                              ? 'bg-zinc-800/90 border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.25)] ring-2 ring-orange-500/40'
                              : 'bg-zinc-950/40 border-zinc-800/60 hover:border-zinc-700'
                          }`}
                        >
                          <div>
                            <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider">PLANO MENSAL FLEXÍVEL</p>
                            <p className="text-xs font-black text-white">Especialista Mensal</p>
                            <p className="text-[9px] text-zinc-500">Sem fidelidade • Cancela quando quiser</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black text-white">R$ 49,90</p>
                            <p className="text-[9px] text-zinc-400">/mês</p>
                          </div>
                        </button>
                      </>
                    )}

                    {/* Opção Discreta para Plano Pro Básico */}
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={() => setShowBasicPro(!showBasicPro)}
                        className="text-[10px] text-zinc-500 hover:text-zinc-400 font-medium underline underline-offset-4 cursor-pointer"
                      >
                        {showBasicPro ? 'Ocultar opções do plano PRO básico' : 'Quer apenas a calculadora básica de orçamentos? Ver planos PRO'}
                      </button>

                      {showBasicPro && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 pt-2 border-t border-zinc-800">
                          <button
                            type="button"
                            onClick={() => setSelectedPlan('monthly')}
                            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                              selectedPlan === 'monthly'
                                ? 'bg-zinc-800 border-emerald-500 shadow-md ring-1 ring-emerald-500/50'
                                : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            <p className="text-[8px] text-zinc-400 font-bold uppercase">PRO MENSAL</p>
                            <p className="text-xs font-black text-white">R$ 19,90/mês</p>
                            <p className="text-[8px] text-zinc-500">Sem fidelidade</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedPlan('pro_annual')}
                            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                              selectedPlan === 'pro_annual'
                                ? 'bg-zinc-800 border-emerald-500 shadow-md ring-1 ring-emerald-500/50'
                                : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            <p className="text-[8px] text-emerald-400 font-bold uppercase">PRO ANUAL (10x)</p>
                            <p className="text-xs font-black text-white">10x 19,50</p>
                            <p className="text-[8px] text-zinc-500">sem juros no cartão</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedPlan('pro_anual_pix')}
                            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer relative ${
                              selectedPlan === 'pro_anual_pix'
                                ? 'bg-emerald-950/70 border-emerald-500 shadow-md ring-1 ring-emerald-500/50'
                                : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            <div className="absolute -top-2 right-1.5 bg-emerald-500 text-black text-[7px] font-black px-1.5 py-0.2 rounded-full uppercase">
                              PIX
                            </div>
                            <p className="text-[8px] text-emerald-400 font-bold uppercase">PRO ANUAL PIX</p>
                            <p className="text-xs font-black text-white">R$ 195,00</p>
                            <p className="text-[8px] text-emerald-400/80">à vista no PIX</p>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sticky Footer */}
                <div className="shrink-0 p-6 pt-3 bg-zinc-900 border-t border-white/5 space-y-3">
                  <div className="bg-white/5 rounded-3xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-0.5">Plano Selecionado</p>
                        <p className="text-sm sm:text-base font-black text-white">
                          {currentPlan.name}
                        </p>
                      </div>
                      <div className={`text-[10px] font-black px-2 py-1 rounded border ${
                        currentPlan.isEnterprise
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                          : currentPlan.isSpecialist 
                            ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' 
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      }`}>
                        {currentPlan.tag}
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => iniciarFluxo(currentPlan.id)}
                      disabled={isLoading}
                      className={`w-full py-3.5 ${
                        currentPlan.isEnterprise
                          ? 'bg-linear-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-500/20'
                          : currentPlan.isSpecialist
                            ? 'bg-linear-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white shadow-orange-500/20'
                            : 'bg-brand hover:bg-brand-dark text-white shadow-brand/20'
                      } font-black rounded-2xl transition-all shadow-xl flex items-center justify-center gap-2 group cursor-pointer uppercase tracking-widest text-xs ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isLoading ? 'A gerar ambiente seguro...' : currentPlan.buttonText}
                      {!isLoading && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                    </button>
                  </div>

                  {errorMessage && (
                    <p className="text-center text-xs text-red-500 font-bold bg-red-500/10 py-2 px-4 rounded-xl border border-red-500/20">{errorMessage}</p>
                  )}

                  <p className="text-center text-[10px] text-zinc-600 font-medium italic">
                    Cancelamento fácil a qualquer momento. Pagamento 100% seguro via Mercado Pago.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
